import React, { useEffect, useState } from "react";
import { addParty, getSingleParty, updateParty } from "../../services/services";
import { toastError, toastSuccess } from "../../utils";
import moment from "moment";
import DatePicker from "react-datepicker";
import CalendarInput from "../Modal/CalendarInput";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { useNavigate, useParams } from "react-router";
import { State } from "country-state-city";

const AddParty = ({ selectedParty, fetchApi }) => {
  const [party, setParty] = useState(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { id } = useParams();

  const indianStates = State.getStatesOfCountry("IN");
  // console.log("indianStates", indianStates);

  const gstTypes = ["Registered", "Unregistered", "Composition", "Consumer"];

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    gstin: null,
    openingBalance: 0,
    openingBalanceType: "toReceive",
    date: new Date(),
    email: "",
    billingAddress: [""],
    shippingAddress: [""],
    gstType: "Unregistered",
    state: "",
    partyType: "customer",
    accountCategory: "Customer",
    city: "",
    assignedTo: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "openingBalance") {
      // Allow empty, or valid decimal number pattern
      const valid =
        value === "" ||
        value === "-" ||
        /^-?\d+\.?\d*$|^-?\d*\.\d+$/.test(value);
      if (!valid) return;

      setFormData((prev) => ({
        ...prev,
        openingBalance: value,
      }));

      setErrors((prev) => ({ ...prev, openingBalance: "" }));
      return;
    }
    if (name === "gstType" && value === "Unregistered") {
      setFormData((prev) => ({
        ...prev,
        gstType: value,
        gstin: null,
      }));
      return;
    }
    // ---- Normal Fields ----
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  const handleAddressChange = (e, type) => {
    setFormData((prev) => ({
      ...prev,
      [type]: [e.target.value],
    }));
  };

  useEffect(() => {
    if (id) getParty();
  }, [id]);

  useEffect(() => {
    if (party) {
      setFormData({
        name: party.name || "",
        mobile: party.mobile || "",
        gstin: party.gstin || null,
        openingBalance: Number(party.openingBalance) || 0,
        openingBalanceType: party.openingBalanceType || "toReceive",
        date: party.date ? new Date(party.date) : new Date(),
        email: party.email || "",
        billingAddress: party.billingAddress?.[0]?.address || "",
        shippingAddress: party.shippingAddress?.[0]?.address || "",
        city: party?.city || "",
        gstType: party.gstType || "Unregistered",
        state: party.state || "Madhya Pradesh",
        partyType: party.partyType || "customer",
      });
    }
  }, [party]);

  const getParty = async () => {
    try {
      const response = await getSingleParty(id);
      setParty(response.data.party);
    } catch (error) {
      toastError(error.response?.data?.message || error.response?.data?.error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (formData.mobile.trim() && !/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Enter a valid 10-digit number";
    }
    if (formData.email) {
      if (formData.email.length > 32) {
        newErrors.email = "Email cannot exceed 32 characters";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Enter a valid email address";
      }
    }

    if (formData.gstin && formData.gstin.trim()) {
      if (formData.gstin.length !== 15) {
        newErrors.gstin = "GSTIN must be exactly 15 characters";
      } else if (
        !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(formData.gstin)
      ) {
        newErrors.gstin = "Invalid GSTIN format (Example: 22AAAAA0000A1Z5)";
      }
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      console.log("handleSubmit", errors);

      return;
    }

    const payload = {
      ...formData,
      date: moment(formData.date).format("YYYY-MM-DD"),
    };

    try {
      const response = await (id
        ? updateParty(id, payload)
        : addParty(payload));

      if (response.data.status) {
        toastSuccess(response.data.message);
        // fetchApi();
        navigate("/party");
        setFormData({
          name: "",
          mobile: "",
          gstin: null,
          openingBalance: 0,
          openingBalanceType: formData.openingBalanceType,
          date: new Date(),
          email: "",
          billingAddress: "",
          shippingAddress: "",
          gstType: "Unregistered",
          state: "Madhya Pradesh",
          partyType: "customer",
        });
        setErrors({});
      }
    } catch (error) {
      console.error("Submit Party Error:", error);

      const backendError = error.response?.data?.message;
      const field = error.response?.data?.field;

      if (field) {
        setErrors((prev) => ({
          ...prev,
          [field]: backendError,
        }));
      } else {
        toastError(backendError || "Error while saving party");
      }
    }
  };
  const handleBulkUpload = () => {
    navigate("/party/bulkupload");
  };

  return (
    <div id="main">
      <div className="d-flex justify-content-between gap-2 mb-3">
        <h2 className="mb-0 custom-title">{id ? "Update" : "Add New"} Party</h2>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2 mb-0"
          onClick={handleBulkUpload}
        >
          {/* Icon for Mobile */}
          <i className="bi bi-upload d-inline d-md-none"></i>

          {/* Text for Desktop */}
          <span className="d-none d-md-inline">Bulk Upload</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} id="partyForm" className="card pb-4">
        <div className="row px-3 pt-3">
          <div className="col-md-6 mb-3">
            <label className="form-label">
              Party Name
              <span className="text-danger ms-1">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
            />
            {errors.name && (
              <div className="invalid-feedback">{errors.name}</div>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Party Type</label>
            <select
              name="partyType"
              value={formData.partyType}
              onChange={handleChange}
              className="form-select"
            >
              <option value="customer">Customer</option>
              <option value="vendor">Vendor</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Mobile</label>
            <input
              type="text"
              name="mobile"
              value={formData.mobile}
              maxLength={10}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 10) {
                  setFormData((prev) => ({ ...prev, mobile: value }));
                  setErrors((prev) => ({ ...prev, mobile: "" }));
                }
              }}
              className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
            // className={`form-control `}
            />
            {errors.mobile && (
              <div className="invalid-feedback">{errors.mobile}</div>
            )}
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => {
                if (e.target.value.length <= 32) {
                  setFormData((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }));
                }
              }}
              className={`form-control ${errors.email ? "is-invalid" : ""}`}
            />
            {errors.email && (
              <div className="invalid-feedback">{errors.email}</div>
            )}
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Opening Balance</label>
            <input
              type="text"
              inputMode="decimal"
              name="openingBalance"
              value={formData.openingBalance}
              onChange={handleChange}
              className="form-control"
            />

            {/* RADIO BUTTONS */}
            <div className="mt-2 d-flex gap-3">
              <label>
                <input
                  type="radio"
                  name="openingBalanceType"
                  value="toReceive"
                  checked={formData.openingBalanceType === "toReceive"}
                  onChange={handleChange}
                />{" "}
                To Receive
              </label>

              <label>
                <input
                  type="radio"
                  name="openingBalanceType"
                  value="toPay"
                  checked={formData.openingBalanceType === "toPay"}
                  onChange={handleChange}
                />{" "}
                To Pay
              </label>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Date</label>
            <DatePicker
              selected={formData.date}
              onChange={(date) =>
                setFormData((prev) => ({
                  ...prev,
                  date: date,
                }))
              }
              customInput={<CalendarInput />}
              dateFormat="dd-MM-yyyy"
            />
          </div>
          {/* <div className="col-md-6 mb-3">
            <label className="form-label">Account Category <span className="text-danger">*</span></label>
            <select
              name="accountCategory"
              value={formData.accountCategory}
              onChange={handleChange}
              className="form-select"
              required
            >
              <option value="Customer">Customer</option>
              <option value="Supplier">Supplier</option>
              <option value="Both">Both (Customer + Supplier)</option>
            </select>
          </div> */}

          {/* City */}
          <div className="col-md-6 mb-3"></div>

          {/* Created By (Read Only) */}
          {/* <div className="col-md-6 mb-3">
            <label className="form-label">Created By</label>
            <input
              type="text"
              value={formData.createdByName || "Loading..."}
              className="form-control"
              disabled
            />
          </div> */}

          {/* Assigned To (Staff Dropdown) */}
          {/* <div className="col-md-6 mb-3">
            <label className="form-label">Assign to Staff</label>
            <select
              name="assignedTo"
              value={formData.assignedTo}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">-- Not Assigned --</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>
                  {emp.name} ({emp.role || "Staff"})
                </option>
              ))}
            </select>
          </div> */}
        </div>

        <div className="row px-3">
          <div className="col-md-6">
            <label className="form-label">GSTIN</label>
            <input
              type="text"
              name="gstin"
              value={formData.gstin}
              maxLength={15}
              // onChange={handleChange}
              onChange={(e) => {
                let value = e.target.value.toUpperCase();

                value = value.replace(/[^0-9A-Z]/g, "");
                if (value.length <= 15) {
                  setFormData((prev) => ({
                    ...prev,
                    gstin: value === "" ? null : value,
                  }));
                  setErrors((prev) => ({ ...prev, gstin: "" }));
                }
              }}
              // className={`form-control`}
              className={`form-control ${errors.gstin ? "is-invalid" : ""}`}
              placeholder="22AAAAA0000A1Z5"
            />

            {errors.gstin && (
              <div className="invalid-feedback">{errors?.gstin}</div>
            )}

            <label className="form-label mt-2">Billing Address</label>
            <input
              type="text"
              name="billingAddress"
              value={formData.billingAddress}
              onChange={(e) => handleAddressChange(e, "billingAddress")}
              className="form-control"
            />

            <label className="form-label mt-2">City</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="form-control"
              placeholder="e.g. City"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">GST Type</label>
            <select
              name="gstType"
              value={formData.gstType}
              onChange={handleChange}
              className="form-select"
            >
              {gstTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <label className="form-label mt-2">Shipping Address</label>
            <input
              type="text"
              name="shippingAddress"
              value={formData.shippingAddress}
              onChange={handleChange}
              className="form-control"
            />

            <label className="form-label mt-2">State</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="form-select"
            >
              {indianStates.map((state) => (
                <option key={state} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </form>

      <div className="text-end p-4">
        <button
          type="button"
          className="btn btn-custom me-2"
          onClick={() => navigate("/party")}
        >
          Cancel
        </button>

        <button type="submit" form="partyForm" className="btn btn-primary">
          {id ? "Update" : "Add"} Party
        </button>
      </div>
    </div>
  );
};

export default headerLayoutWrapper(AddParty);
